import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { OnlineIndicator } from '@/components/OnlineIndicator';
import { Search, UserX, Shield, ShieldAlert, AlertTriangle, Power } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

export default function ActiveSessionsPage() {
  const { user } = useAuth();
  const [data, setData] = useState({ online_count: 0, total_users: 0, users: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [togglingMaint, setTogglingMaint] = useState(false);
  const [kickTarget, setKickTarget] = useState(null);
  const [kicking, setKicking] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sessRes, maintRes] = await Promise.all([
        api.get('/api/sessions/active'),
        api.get('/api/maintenance/status')
      ]);
      setData(sessRes.data);
      setMaintenanceMode(maintRes.data.maintenance_mode);
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const id = setInterval(load, 15000); return () => clearInterval(id); }, [load]);

  const handleKick = async () => {
    if (!kickTarget) return;
    setKicking(true);
    try {
      await api.post(`/api/sessions/kick/${kickTarget.id}`);
      toast.success(`Session terminated for ${kickTarget.name}`);
      setKickTarget(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to kick'); }
    setKicking(false);
  };

  const handleToggleMaintenance = async () => {
    setTogglingMaint(true);
    try {
      const res = await api.post('/api/maintenance/toggle');
      setMaintenanceMode(res.data.maintenance_mode);
      toast.success(`Maintenance mode ${res.data.maintenance_mode ? 'enabled' : 'disabled'}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    setTogglingMaint(false);
  };

  const filtered = data.users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.role?.toLowerCase().includes(search.toLowerCase())
  );

  const roleColor = {
    admin: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
    account_manager: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    sponsor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    spo_viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  };

  return (
    <div data-testid="active-sessions-page" className="space-y-6">
      {/* Maintenance Mode Card */}
      <Card className={`border-2 ${maintenanceMode ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'} shadow-none`}>
        <CardContent className="p-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${maintenanceMode ? 'bg-amber-200 dark:bg-amber-800' : 'bg-gray-100 dark:bg-gray-800'}`}>
              {maintenanceMode ? <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400" /> : <Power size={20} className="text-gray-500 dark:text-gray-400" />}
            </div>
            <div>
              <p className="section-title">Maintenance Mode</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {maintenanceMode ? 'Active — Only admins can access the platform' : 'Inactive — All users can access normally'}
              </p>
            </div>
          </div>
          <Button
            data-testid="toggle-maintenance-btn"
            variant={maintenanceMode ? 'destructive' : 'outline'}
            size="sm"
            className="text-xs h-9 px-5"
            onClick={handleToggleMaintenance}
            disabled={togglingMaint}
          >
            {togglingMaint ? 'Switching...' : maintenanceMode ? 'Disable Maintenance' : 'Enable Maintenance'}
          </Button>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
              <Shield size={18} className="text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="page-title">{data.online_count}</p>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Online Now</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-none">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
              <ShieldAlert size={18} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="page-title">{data.total_users}</p>
              <p className="text-[11px] text-gray-400 uppercase tracking-wider">Total Users</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <Input
          data-testid="session-search"
          placeholder="Search users..."
          className="pl-9 h-9 text-sm border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Users Table */}
      <Card className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-gray-200 dark:border-gray-700 hover:bg-transparent">
                <TableHead className="text-xs text-gray-400 font-medium w-10">Status</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">User</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">Email</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">Role</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium">Last Active</TableHead>
                <TableHead className="text-xs text-gray-400 font-medium w-20">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400 text-sm">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-400 text-sm">No users found</TableCell></TableRow>
              ) : filtered.map(u => (
                <TableRow key={u.id} data-testid={`session-row-${u.id}`} className="border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <TableCell className="text-center"><OnlineIndicator isOnline={u.is_online} /></TableCell>
                  <TableCell className="text-sm font-medium text-gray-800 dark:text-gray-200">{u.name}</TableCell>
                  <TableCell className="text-sm text-gray-500 dark:text-gray-400">{u.email}</TableCell>
                  <TableCell>
                    <Badge className={`text-[10px] pointer-events-none ${roleColor[u.role] || 'bg-gray-100 text-gray-600'}`}>
                      {u.role?.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-gray-500 dark:text-gray-400">
                    {u.last_seen ? new Date(u.last_seen).toLocaleString() : 'Never'}
                  </TableCell>
                  <TableCell>
                    {u.is_online && u.id !== user?.id && (
                      <Button
                        data-testid={`kick-user-${u.id}`}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950 h-7 text-xs px-2"
                        onClick={() => setKickTarget(u)}
                      >
                        <UserX size={14} className="mr-1" /> Kick
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Kick Confirmation Dialog */}
      <Dialog open={!!kickTarget} onOpenChange={() => setKickTarget(null)}>
        <DialogContent className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-gray-100">Terminate Session</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Are you sure you want to terminate the session for <strong className="text-gray-900 dark:text-gray-100">{kickTarget?.name}</strong>? 
            They will be logged out immediately and need to log in again.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setKickTarget(null)} className="border-gray-300">Cancel</Button>
            <Button variant="destructive" size="sm" onClick={handleKick} disabled={kicking}>
              {kicking ? 'Terminating...' : 'Terminate Session'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
